/**
 * Program Resources Service
 * Resource allocations, roles, and basic management
 */
import { apiClient } from './api'

export interface ResourceAllocation {
  id: number
  program_id: number
  project_id?: number
  user_id?: number
  role: string
  allocation_percentage: number
  start_date: string
  end_date?: string
  utilization_rate?: number
  metadata?: Record<string, unknown>
}

export interface ResourceRole {
  id: number
  program_id: number
  role_name: string
  required_count: number
  allocated_count: number
  open_positions: number
  status: string
  metadata?: Record<string, unknown>
}

export interface ResourceUtilization {
  total_resources: number
  utilization_rate: number
  open_positions: number
  resource_cost?: number
}

export interface CapacityAnalysisItem {
  role_name: string
  required_count: number
  allocated_count: number
  open_positions: number
  gap: number
  status: string
  utilization_rate: number
}

export interface CapacityAnalysis {
  program_id: number
  capacity_analysis: CapacityAnalysisItem[]
  total_required: number
  total_allocated: number
  total_gap: number
}

export const programResourcesService = {
  /**
   * List resource allocations
   */
  async listAllocations(
    programId: number,
    projectId?: number,
    userId?: number,
    role?: string
  ): Promise<ResourceAllocation[]> {
    const params: Record<string, unknown> = {}
    if (projectId) params.project_id = projectId
    if (userId) params.user_id = userId
    if (role) params.role = role
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/resources/allocations`,
      { params }
    )
    return response.data
  },

  /**
   * Create resource allocation
   */
  async createAllocation(programId: number, data: Partial<ResourceAllocation>): Promise<ResourceAllocation> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/resources/allocations`,
      data
    )
    return response.data
  },

  /**
   * Update resource allocation
   */
  async updateAllocation(
    programId: number,
    allocationId: number,
    data: Partial<ResourceAllocation>
  ): Promise<ResourceAllocation> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/resources/allocations/${allocationId}`,
      data
    )
    return response.data
  },

  /**
   * Delete resource allocation
   */
  async deleteAllocation(programId: number, allocationId: number): Promise<void> {
    await apiClient.delete(
      `/api/v1/programs/${programId}/resources/allocations/${allocationId}`
    )
  },

  /**
   * Get resource utilization
   */
  async getUtilization(programId: number): Promise<ResourceUtilization> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/resources/utilization`
    )
    return response.data
  },

  /**
   * List resource roles
   */
  async listRoles(programId: number, status?: string): Promise<ResourceRole[]> {
    const params: Record<string, unknown> = {}
    if (status) params.status = status
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/resources/roles`,
      { params }
    )
    return response.data
  },

  /**
   * Create or update resource role
   */
  async createRole(programId: number, data: Partial<ResourceRole>): Promise<ResourceRole> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/resources/roles`,
      data
    )
    return response.data
  },

  /**
   * Get capacity analysis
   * Calculates capacity based on roles and allocations
   */
  async getCapacityAnalysis(programId: number): Promise<CapacityAnalysis> {
    const [roles, allocations] = await Promise.all([
      this.listRoles(programId),
      this.listAllocations(programId)
    ])

    const capacityAnalysis: CapacityAnalysisItem[] = roles.map(role => {
      const roleAllocations = allocations.filter(a => a.role === role.role_name)
      const allocatedCount = roleAllocations.length
      const gap = role.required_count - allocatedCount
      const utilizationRate = role.required_count > 0 
        ? (allocatedCount / role.required_count) * 100 
        : 0

      let status = 'adequate'
      if (gap > 0) {
        status = 'shortage'
      } else if (gap < 0) {
        status = 'surplus'
      }

      return {
        role_name: role.role_name,
        required_count: role.required_count,
        allocated_count: allocatedCount,
        open_positions: role.open_positions,
        gap,
        status,
        utilization_rate: utilizationRate
      }
    })

    const totalRequired = capacityAnalysis.reduce((sum, ca) => sum + ca.required_count, 0)
    const totalAllocated = capacityAnalysis.reduce((sum, ca) => sum + ca.allocated_count, 0)
    const totalGap = totalRequired - totalAllocated

    return {
      program_id: programId,
      capacity_analysis: capacityAnalysis,
      total_required: totalRequired,
      total_allocated: totalAllocated,
      total_gap: totalGap
    }
  },
}

